/*
 * Task model constants and helpers
 * Purpose: Define the task data shape shared by task pages, account task views, and note follow-up flows.
 * Implementation notes: Tasks expose semantic status, priority, and source labels while supporting multi-assignee delivery.
 * Recent changes: Aligns Description-derived task titles with the API/database 255-character contract.
 * - Added transient assignment-email choice to the create mutation contract.
 * - Added shared first-line Description title derivation for task creation.
 * - Added shared required and past-date validation so every editable task surface follows one due-date contract.
 * - Added soft-delete metadata so active and deleted task views can share one task contract.
 * - Added shared workflow helpers for UI status labels, closed-state checks, task date input guards, and task editability checks.
 */

export const TASK_STATUSES = ['Open', 'Waiting', 'Done', 'Canceled'] as const;
export type TaskStatus = typeof TASK_STATUSES[number];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  Open: 'Todo',
  Waiting: 'In Progress',
  Done: 'Completed',
  Canceled: 'Cancel'
};

export const TASK_PRIORITIES = ['High', 'Medium', 'Low'] as const;
export type TaskPriority = typeof TASK_PRIORITIES[number];

export const TASK_SOURCE_TYPES = ['Manual', 'Note'] as const;
export type TaskSourceType = typeof TASK_SOURCE_TYPES[number];

export interface TaskAssignee {
  id: number;
  name: string;
  email?: string;
}

export interface Task {
  id: number;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  accountId: number;
  accountName: string;
  contactId: number | null;
  contactName?: string;
  sourceNoteId: number | null;
  sourceNoteTitle?: string;
  sourceType: TaskSourceType;
  sourceLabel: string;
  createdByUserId: number;
  createdByUserName?: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  deletedAt: string | null;
  deletedByUserId: number | null;
  deletedByUserName?: string;
  assignees: TaskAssignee[];
  assigneeIds: number[];
  assigneeNames: string[];
  assigneeName: string;
}

export interface TaskFilters {
  accountId?: number;
  contactId?: number;
  assigneeId?: number;
  status?: TaskStatus;
  includeClosed?: boolean;
  includeDeleted?: boolean;
  deletedOnly?: boolean;
}

export interface TaskMutationPayload {
  title: string;
  description?: string;
  accountId: number;
  contactId?: number | null;
  sourceNoteId?: number | null;
  dueDate?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  sourceType?: TaskSourceType;
  assigneeIds?: number[];
  sendEmail?: boolean;
}

export const getTaskStatusLabel = (status: TaskStatus): string => {
  return TASK_STATUS_LABELS[status];
};

export const isTaskClosed = (status: TaskStatus): boolean => {
  return status === 'Done' || status === 'Canceled';
};

export const getTodayDateValue = (now = new Date()): string => {
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isTaskDueDateInPast = (dueDate: string | null | undefined, today = getTodayDateValue()): boolean => {
  return Boolean(dueDate && dueDate < today);
};

export const TASK_DUE_DATE_REQUIRED_ERROR = 'Due date is required.';
export const TASK_DUE_DATE_PAST_ERROR = 'Due date cannot be in the past.';
export const TASK_DERIVED_TITLE_MAX_LENGTH = 255;
export const TASK_DESCRIPTION_TITLE_REQUIRED_ERROR = 'Enter a task title.';

export const getTaskTitleFromDescription = (description: string | null | undefined): string => {
  const firstLine = (description ?? '').split(/\r?\n/, 1)[0].trim();
  return firstLine.slice(0, TASK_DERIVED_TITLE_MAX_LENGTH);
};

export const getTaskDueDateValidationError = (
  dueDate: string | null | undefined,
  today = getTodayDateValue()
): string | null => {
  if (!dueDate) {
    return TASK_DUE_DATE_REQUIRED_ERROR;
  }

  return isTaskDueDateInPast(dueDate, today) ? TASK_DUE_DATE_PAST_ERROR : null;
};

export const canMutateTask = (task: Pick<Task, 'createdByUserId' | 'assigneeIds'>, currentUserId: number | null | undefined): boolean => {
  if (!currentUserId) {
    return false;
  }

  return task.createdByUserId === currentUserId || task.assigneeIds.includes(currentUserId);
};

export const getTaskAssigneeLabel = (assignees: Pick<TaskAssignee, 'name'>[] | string[]): string => {
  const names = assignees.map((assignee) => typeof assignee === 'string' ? assignee : assignee.name).filter(Boolean);

  if (!names.length) {
    return 'Unassigned';
  }

  return names.join(', ');
};

const TASK_SOURCE_NOTE_TITLE_MAX_LENGTH = 30;

interface TaskSourceLabelOptions {
  showNoteId?: boolean;
}

const shouldShowTaskSourceNoteId = (): boolean => {
  return !import.meta.env.PROD;
};

const formatTaskSourceNoteTitle = (sourceNoteTitle?: string | null): string | null => {
  const trimmedTitle = sourceNoteTitle?.trim();
  if (!trimmedTitle) {
    return null;
  }

  return trimmedTitle.length > TASK_SOURCE_NOTE_TITLE_MAX_LENGTH
    ? `${trimmedTitle.slice(0, TASK_SOURCE_NOTE_TITLE_MAX_LENGTH).trimEnd()}...`
    : trimmedTitle;
};

export const getTaskSourceLabel = (
  sourceType: TaskSourceType,
  sourceNoteId?: number | null,
  sourceNoteTitle?: string | null,
  options: TaskSourceLabelOptions = {}
): string => {
  if (sourceType === 'Note') {
    const showNoteId = options.showNoteId ?? shouldShowTaskSourceNoteId();
    const formattedNoteTitle = formatTaskSourceNoteTitle(sourceNoteTitle);
    if (formattedNoteTitle) {
      return showNoteId && sourceNoteId
        ? `From note #${sourceNoteId}: ${formattedNoteTitle}`
        : `From note: ${formattedNoteTitle}`;
    }

    return showNoteId && sourceNoteId ? `From note #${sourceNoteId}` : 'From note';
  }

  return 'Manual';
};
